import {useAuth} from "../../providers/authprovider";
import React, {useEffect, useState} from "react";
import {Button, Form, FormControl} from "react-bootstrap";
import {Typeahead} from "react-bootstrap-typeahead";
import {AddressDisplay} from "../../components/AddressForm";
import {v4 as uuidv4} from "uuid";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft, faEye, faSave, faSpinner} from "@fortawesome/free-solid-svg-icons";
import useAlert from "../../components/CustomAlert";
import ProvideFormContext, {BuildHtmlElement, DateInput} from "../../components/forms/formElements"
import RajcompanyLogo from "../../components/RajcompanyLogo";
import OfferMigrationDialog, { migrateOfferTablesToPriceList } from "./OfferMigrationDialog";



function replaceInTree(elements, element){

    function updateValue(nodes, element){
        for(let i in nodes){
            const node = nodes[i];

            if(node.name === element.name){
                nodes[i].value = element.value
                return;
            }

            if(node.children && Array.isArray(node.children)){
                updateValue(node.children, element)
            }
        }
    }

    updateValue(elements, element);


    return [...elements]
}

function findOfferPriceTables(elements){
    let tables = [];
    for(let element of elements){
        if(element.type === "table_price_list")
            tables.push(element);

        if(element.children && Array.isArray(element.children)){
            tables = tables.concat(findOfferPriceTables(element.children));
        }
    }

    return tables;
}

function hasMigratableOfferTable(elements){
    for(let element of elements){
        if(element.type === "table_offer")
            return true;

        if(element.name === "table_assortments")
            return true;

        if(element.type === "table")
            return true;

        if(element.children && Array.isArray(element.children)){
            if(hasMigratableOfferTable(element.children))
                return true;
        }
    }

    return false;
}

function getOfferPriceRowValue(row, totalMultiplier){
    return `${(Number(row.quantity) * Number(row.unitPrice) * Number(totalMultiplier)).toFixed(2)} zł`;
}

function applyOfferPriceRowsToTable(offer, offerPriceRows){
    const tables = findOfferPriceTables(offer.elements);

    tables.forEach((table)=>{
        if(table.totalMultiplier === undefined)
            table.totalMultiplier = 1.0;

        table.value = offerPriceRows.map((offerPriceRow)=>{
            return table.columns.map((column)=>{
                if(column.type === "sum")
                    return {
                        ...column,
                        value: getOfferPriceRowValue(offerPriceRow, table.totalMultiplier),
                    };

                if(!Object.prototype.hasOwnProperty.call(offerPriceRow, column.name))
                    return { ...column };

                return {
                    ...column,
                    value: offerPriceRow[column.name],
                };
            });
        });

        const sumColumnIndex = table.columns.findIndex((column) => column.type === "sum");
        table.total = `${table.value.reduce((total, row)=>{
            return total + Number(row[sumColumnIndex].value.replace(" zł", ""));
        }, 0).toFixed(2)} zł`;
    });

    return tables.length > 0;
}

export default function CreateOfferForm(props) {

    const {shortBuildings, copy} = props;
    const {offerId, offerType, templateId} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const {alert, hideAlert, showAlert} = useAlert();
    const offerPriceRows = location.state?.offerPriceRows;
    const migrateOfferTablesBeforeImport = location.state?.migrateOfferTablesToPriceList === true;
    const [assortments, setAssortments] = useState();
    const [clients, setClients] = useState();
    const [companies, setCompanies] = useState([]);
    const [workers, setWorkers] = useState();
    const [dirtyState, setDirty] = useState(false);
    const [downloading, setDownloading] = useState(false)
    const [showMigrationDialog, setShowMigrationDialog] = useState(false);
    const {assortments:{get: getAssortments},users, clients : {getShort : getShortClientsInfo, getClient}, offers, rajcompanies, viewPdf} = useAuth();
    const [formData, setFormData] = useState()


    useEffect(()=>{

        getAssortments(({success, error, content})=>{
            if(!success)
                return showAlert(error);
            setAssortments(content);
        })

        getShortClientsInfo((response)=>{
            if(!response.success)
                return;

            setClients(response.content);
        })

        users.get(({success, error, content})=>{

            if(success === true) {
                setWorkers(content)
                return;
            }

            showAlert(error);
        }, 'offers');

        rajcompanies.get(null, ({success, error, content})=>{
            if(!success)
                return showAlert(error);

            setCompanies(content);
        })

    }, [])

    useEffect(()=>{

        if(offerId) {
            offers.getOffer(offerId, ({success, error, content}) => {
                const offer = content;

                if(copy)
                    offer.id = uuidv4();

                setFormData(offer)
            })
            return
        }

        if(templateId) {
            offers.templates.getById(templateId, ({success, error, content})=>{
                if(!success)
                    return showAlert(error);

                // Nowa oferta na bazie zapisanego szablonu — pola właściwe ofercie zaczynamy od nowa.
                let offer = {
                    id: uuidv4(),
                    client: content.client ?? undefined,
                    date: new Date().toJSON().substring(0,10),
                    name:'',
                    toWhom: content.toWhom ?? '',
                    type: content.type,
                    building:{},
                    elements: content.elements
                };

                if(migrateOfferTablesBeforeImport)
                    offer.elements = migrateOfferTablesToPriceList(offer.elements).elements;

                const importedOfferPriceRows = offerPriceRows
                    ? applyOfferPriceRowsToTable(offer, offerPriceRows)
                    : false;

                setFormData(offer)
                if(importedOfferPriceRows)
                    setDirty(true);
            })
            return
        }

        offers.getTemplate(offerType, ({success, error, content})=>{
            if(success) {
                let offer = {
                    id: uuidv4(),
                    client: undefined,
                    date: new Date().toJSON().substring(0,10),
                    name:'',
                    toWhom: '',
                    type: offerType,
                    building:{},
                    elements: content.elements
                };

                if(migrateOfferTablesBeforeImport)
                    offer.elements = migrateOfferTablesToPriceList(offer.elements).elements;

                const importedOfferPriceRows = offerPriceRows
                    ? applyOfferPriceRowsToTable(offer, offerPriceRows)
                    : false;

                setFormData(offer)
                if(importedOfferPriceRows)
                    setDirty(true);
                return;
            }

            if(error)
                return showAlert(error)

            return showAlert("Wystapił nieznany błąd.")
        })

    }, [offerId])

    useEffect(()=>{
        if(!formData || formData.leadingCompanyId)
            return;

        const defaultCompany = companies.find((company)=>company.default);

        if(!defaultCompany)
            return;

        setDirty(true);
        setFormData((prev)=>({...prev, leadingCompanyId: defaultCompany.id}));
    }, [formData, companies])

    const data = {
        workers: workers,
        assortments: assortments,
        filter: null,
        onChange : function(element){
            setFormData((form)=>{
                setDirty(true)
                form.elements = replaceInTree(form.elements, element)
                return {...form}
            })
        },
        onRemoveSection : function(element){
            window.alert("Ta funkcja jest wyłączona w ofertach.")
        },
        tableTextAsTextarea: true
    }

    const handleCloseEdition = ()=>{
        if(dirtyState === false)
            return navigate(-1);

        let close = window.confirm("Formularz nie został zapisany. Zamknięcie spowoduje utratę danych. Czy chcesz zamknąć ?");

        if(close === false)
            return;

        return navigate(-1);
    }

    const handleSaveOffer = ()=>{
            offers.add(formData, ({success, error, content})=>{

                if(success) {
                    setFormData(content)

                    if(props.handleSave)
                        props.handleSave(content);

                    return setDirty(false);
                }



                showAlert(error);
            })
    }

    const handleDisplayProtocol = ()=>{

        hideAlert()

        offers.displayOffer(formData, ({success, error, content})=>{
            if(success)
                return viewPdf(content)

            showAlert(error)
        })
    }

    const handleSetOfferDate = (date)=>{
        setDirty(true)
        setFormData({...formData, date: date})
    }

    const handleSelectClient = (options)=>{

        if (options.length <= 0)
            return;

        let {id} = options[0];

        getClient(id, (response) => {
            if (response.success) {
                setDirty(true)
                setFormData({...formData, client:response.content})
            }
        })
    }

    const handleSelectLeadingCompany = (event)=>{
        setDirty(true)
        setFormData({...formData, leadingCompanyId: event.target.value === "" ? null : event.target.value})
    }

    const handleSelectBuilding = (building)=>{
        setDirty(true)
        setFormData({...formData, building: building})
    }

    const handleSetOfferName = (event)=>{
        setDirty(true)
        setFormData({...formData, name:event.target.value})
    }


    const handleSetToWhom = (event)=>{
        setDirty(true)
        setFormData({...formData, toWhom:event.target.value})
    }

    const handleMigrateOffer = (elements) => {
        setFormData({
            ...formData,
            elements: elements,
        });
        setDirty(true);
        setShowMigrationDialog(false);
    }

    const canMigrateOffer = formData && hasMigratableOfferTable(formData.elements);
    const leadingCompany = companies.find((company)=>company.id === formData?.leadingCompanyId);

    return (
        <ProvideFormContext value={data} >
            <div className={"position-relative offer-form"}>
                <div className={"position-sticky bg-light"} style={{top:0, left:0, zIndex:99}}>
                    {alert()}
                    <div className={"d-flex justify-content-end"}>
                        
                        <Button variant={"outline-secondary"} className={"m-2"} onClick={handleCloseEdition} ><FontAwesomeIcon icon={faArrowLeft} /> Powrót</Button>
                        {canMigrateOffer ? (
                            <Button variant={"outline-primary"} className={"m-2"} onClick={() => setShowMigrationDialog(true)}>
                                Migracja oferty
                            </Button>
                        ) : null}
                        <Button variant={"success"} className={"m-2"} onClick={handleSaveOffer} disabled={!dirtyState} ><FontAwesomeIcon icon={faSave}  /> Zapisz</Button>
                        <Button variant={"info"} className={"m-2"} onClick={handleDisplayProtocol} >
                            {
                                downloading ? <FontAwesomeIcon icon={faSpinner} spin={true} /> : <><FontAwesomeIcon icon={faEye} /> Podgląd</>
                            }
                        </Button>
                    </div>
                </div>
                <OfferMigrationDialog
                    show={showMigrationDialog}
                    onHide={() => setShowMigrationDialog(false)}
                    elements={formData?.elements}
                    onMigrate={handleMigrateOffer}
                />
                {
                    formData &&
                    <>
                        <div className={'protocol_section'}>
                            <h4 className={"w-100 text-center"}>Podstawowe dane </h4>
                            <Form.Group className="m-2" controlId={"element.name"}>
                                <Form.Label>Nazwa oferty</Form.Label>
                                <Form.Control   value={formData.name}  onChange={handleSetOfferName} maxLength={20} placeholder={"wpisz krótko nazwę oferty"} />
                                <Form.Text  muted>
                                    Pozostało { 20 - (formData.name?.length ?? 0)} znaków
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className={"m-2"} controlId={"leading_company"}>
                                <Form.Label>Spółka obsługująca ofertę</Form.Label>
                                <Form.Select value={formData.leadingCompanyId ?? ""} onChange={handleSelectLeadingCompany}>
                                    <option value={""}>Wybierz spółkę...</option>
                                    {
                                        companies.map((company)=>{
                                            return <option key={company.id} value={company.id}>{company.full_name}</option>
                                        })
                                    }
                                </Form.Select>
                            </Form.Group>

                            {
                                leadingCompany &&
                                <div className={"border p-1 m-2 d-flex justify-content-between align-items-center gap-3"}>
                                    <div>
                                        <b>Dane spółki</b>
                                        <div>{leadingCompany.full_name}</div>
                                        <div><AddressDisplay address={leadingCompany.address} /></div>
                                        {leadingCompany.nip && <div>NIP {leadingCompany.nip}</div>}
                                        {leadingCompany.regon && <div>REGON {leadingCompany.regon}</div>}
                                        {leadingCompany.krs && <div>KRS {leadingCompany.krs}</div>}
                                    </div>
                                    {
                                        leadingCompany.has_logo &&
                                        <RajcompanyLogo companyId={leadingCompany.id} hasLogo height={60} />
                                    }
                                </div>
                            }

                            <Form.Group className={"m-2"}>
                                <Form.Label>Wybierz kontrahenta</Form.Label>
                                <Typeahead
                                    id="basic-typeahead-single"
                                    labelKey="name"
                                    onChange={handleSelectClient}
                                    options={clients}
                                    placeholder="Wybierz klienta..."
                                    defaultInputValue={formData.client?.name}
                                />
                            </Form.Group >

                            {
                                formData.client &&
                                <div >

                                    <div className={"border p-1 m-2"}>
                                        <b>Dane kontrahenta</b>
                                        <div>{formData.client.name}</div>
                                        <div><AddressDisplay address={formData.client.address} /></div>
                                        <div>NIP {formData.client.nip}</div>
                                    </div>
                                </div>

                            }

                            <Form.Group className={"m-2"}>
                                <Form.Label>Do kogo:</Form.Label>
                                <FormControl

                                    labelKey="toWhom"
                                    onChange={handleSetToWhom}
                                    placeholder="Wprowadź odbiorcę"
                                    value={formData.toWhom}
                                />
                            </Form.Group >


                            <SelectBuilding shortBuildings={shortBuildings} clientId={formData.client?.id} value={formData.building?.name} onChange={handleSelectBuilding}/>

                            <DateInput value={{name:"date", class:"", label:"Data oferty", value:formData.date}} onChange={handleSetOfferDate} />
                        </div>

                        {
                            formData.elements?.map((element, i)=>{
                                return <BuildHtmlElement key={i} element={element}  />
                            })
                        }
                    </>
                }
            </div>


        </ProvideFormContext>
    )

}

function SelectBuilding(props){

    const {shortBuildings, clientId, value, onChange} = props;

    const handleSelectBuilding = (buildings)=>{
        onChange(buildings[0] ?? null)

    }

    return (
        <Form.Group className={"m-2"}>
            <Form.Label>Obiekt</Form.Label>
            <Typeahead
                disabled={!clientId}
                clearButton
                id="select_building"
                labelKey="name"
                onChange={handleSelectBuilding}
                options={shortBuildings}
                placeholder="Wybierz obiekt..."
                defaultInputValue={value}
            />
        </Form.Group >
    )
}


